import {Injectable}                 from '@angular/core';
import {BestScanData}               from '../../types/scan.types';
import {ApplicationSettingsService} from './application-settings.service';
import {ApTranslationService}       from '../../ap-utils/service/ap-translation.service';
import {ApStringService}            from '../../ap-utils/service/ap-string.service';
import {NotifyStore}                from '../../stores/dialog/notify.store';
import {OperationModesEnum}         from '../../stores/common/operation-modes.store';
import {ApplicationModesEnum}       from '../../stores/common/appl-modes.store';
import {GetGrowthStageData}         from '../../types/n-fertilization-plant-protection.types';
import {
  PpConstants
}                                   from '../../entry-components/plant-protection-entry-components/planning-wizard/pp-planning-wizard.types';
import {Create}                     from 'ts-tooling';
import {ApColumnState}              from '../../ap-interface/enums/ap-column-state.enum';
import {IGridStatus}                from '../../ap-interface/interfaces/ap-grid-status.interface';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;
import IApValidationResult = Data.Api.Validation.IApValidationResult;

@Injectable({providedIn: 'root'})
export class NFertilizationPlantProtectionService {
  constructor(private notifyStore: NotifyStore,
              private stringService: ApStringService,
              private translationService: ApTranslationService,
              private applicationSettingsService: ApplicationSettingsService) {
  }

  /**
   * Determines the grid status based on the operation mode
   * Returns yellow status for constant operation mode and green status for variable modes
   * @param operationModeEnum Operation mode to determine status for
   * @returns Grid status object with state, class and description
   * @private
   */
  public getStatusForItemOnOverviewStep(operationModeEnum: OperationModesEnum): IGridStatus {
    if (operationModeEnum === OperationModesEnum.Constant) {
      return {
        state: ApColumnState.Yellow,
        class: 'ap-status-yellow',
        description: 'Global__RateConst'
      };
    }
    return {
      state: ApColumnState.Green,
      class: 'ap-status-green',
      description: 'Global__RateVariable'
    };
  }

  /**
   * Converts operation mode to corresponding application mode
   * @param operationModeKey The current operation mode
   * @returns Corresponding application mode
   * @public
   */
  public getApplicationModeFromOperationMode(operationModeKey: OperationModesEnum | undefined): ApplicationModesEnum {
    if (!operationModeKey) {
      return undefined;
    }
    if (operationModeKey === OperationModesEnum.Constant) {
      return ApplicationModesEnum.Constant;
    }
    return ApplicationModesEnum.Variable;
  }

  /**
   * Determines the operation mode for planning based on scan data and application settings
   * @param operationMode Current operation mode
   * @param bestScanData Best scan data
   * @param applicationSettings Application-specific settings
   * @returns Adjusted operation mode for planning
   * @public
   */
  public getOperationModeForPlan(operationMode: OperationModesEnum, bestScanData: BestScanData,
                                 applicationSettings: IApplicationSettings | undefined): OperationModesEnum {
    if (applicationSettings && this.isOperationModeAnyOfMaps(operationMode)) {
      return this.applicationSettingsService.isApplicationSettingValidForGrowthStage(bestScanData.Ec, applicationSettings)
        ? operationMode
        : OperationModesEnum.Constant;
    }
    return this.isOperationModeAnyOfMapsOrConstant(operationMode) && bestScanData.Status.state !== 1
      ? OperationModesEnum.Constant
      : operationMode;
  }

  /**
   * Determines the application mode for planning based on operation mode and scan data
   * @param operationMode Current operation mode
   * @param applicationMode Current application mode
   * @param bestScanData Best scan data
   * @returns Adjusted application mode for planning
   * @public
   */
  public getApplicationModeForPlan(operationMode: OperationModesEnum, applicationMode: ApplicationModesEnum,
                                   bestScanData: BestScanData): ApplicationModesEnum {
    if (operationMode === OperationModesEnum.Constant
      || (this.isOperationModeAnyOfMaps(operationMode) && bestScanData.Status.state !== 1)) {
      return ApplicationModesEnum.Constant;
    }
    return applicationMode;
  }

  /**
   * Checks if the operation mode is a map-based or constant mode
   * @param operationMode Operation mode to check
   * @returns Boolean indicating if mode is map-based or constant
   * @public
   */
  public isOperationModeAnyOfMapsOrConstant(operationMode: OperationModesEnum): boolean {
    if (this.isOperationModeAnyOfMaps(operationMode)) {
      return true;
    }
    return operationMode === OperationModesEnum.Constant;
  }

  /**
   * Checks if the operation mode is an application map or absolute mode
   * @param operationModeKey Operation mode to check
   * @returns Boolean indicating if mode is an application map or absolute mode
   * @public
   */
  public isOperationModeAnyOfMaps(operationModeKey: OperationModesEnum): boolean {
    if (!operationModeKey) {
      return false;
    }
    return operationModeKey === OperationModesEnum.ApplicationMapAbsolute
      || operationModeKey === OperationModesEnum.ApplicationMap
      || operationModeKey === OperationModesEnum.PpTargetMapPerField
      || operationModeKey === OperationModesEnum.PPTargetMapPerPlan;
  }

  /**
   * Displays a warning notification if some scans are outside application settings
   * @param scansData Array of best scan data
   * @param applicationSettings Application-specific settings
   * @public
   */
  public onApplicationSettingsTextWarning(scansData: BestScanData[], applicationSettings: IApplicationSettings): void {
    if (!scansData || scansData.length <= 0 || !applicationSettings) {
      return;
    }
    let warningText: string;
    const validSelectedEcValues = scansData.filter(x => {
      return this.applicationSettingsService.isApplicationSettingValidForGrowthStage(x.Ec, applicationSettings);
    });
    if (validSelectedEcValues.length > 0 && scansData.length !== validSelectedEcValues.length) {
      const translatedText = this.translationService.translate('PlanCreationOutsideOfLimitation');
      warningText = this.stringService.format(translatedText, validSelectedEcValues.length, scansData.length);
    } else {
      return;
    }
    this.notifyStore.addMessage({
      Level: ApValidationLevel.Warning,
      Counter: 1,
      ErrorKey: warningText,
      DisplayTimeout: 5
    } as IApValidationResult);
  }

  /**
   * Filters best scans based on application settings
   * @param scansData Array of best scan data
   * @param applicationSettings Application-specific settings
   * @returns Filtered array of valid best scans
   * @public
   */
  public getValidBestScansByApplicationSettings(scansData: BestScanData[],
                                                applicationSettings: IApplicationSettings): BestScanData[] {
    return scansData?.filter(x => {
      return x.Status.state === ApColumnState.Green
        && this.applicationSettingsService.isApplicationSettingValidForGrowthStage(x.Ec, applicationSettings);
    }) ?? [];
  }

  /**
   * Extracts unique growth stages from items
   * @param items Array of growth stage data
   * @returns Array of unique EC values
   * @public
   */
  public getGrowStages(items: GetGrowthStageData[]): number[] {
    const ec: number[] = [];
    items.map(item => {
      if (item?.ScanData?.Ec != null && item.ScanData.Ec >= 0
        && ec.every(x => x !== item.ScanData.Ec)) {
        ec.push(item.ScanData.Ec);
      }
      if (item?.CropData?.EcValue != null && item.CropData.EcValue >= 0
        && ec.every(x => x !== item.CropData.EcValue)) {
        ec.push(item.CropData.EcValue);
      }
    });
    return ec;
  }

  /**
   * Calculates slope based on operation mode and other parameters
   * @param operationModeKey Current operation mode
   * @param controlFactor Slope value
   * @param isRape Flag indicating if the crop is rapeseed
   * @param applicationSettings Application-specific settings
   * @returns Calculated slope value
   * @public
   */
  public getSlope(operationModeKey: OperationModesEnum, controlFactor: number | undefined,
                  isRape: boolean, applicationSettings: IApplicationSettings | undefined): number {
    if (!Number.isFinite(controlFactor)) {
      controlFactor = applicationSettings?.Parameter?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
    if (applicationSettings && this.isOperationModeAnyOfMaps(operationModeKey)) {
      return controlFactor;
    }
    return operationModeKey === OperationModesEnum.Constant
      ? 0
      : isRape ? 1 : controlFactor;
  }

  /**
   * Calculates the area-weighted average of the provided items
   * @param items Array of items with area and average values
   * @returns Calculated area-weighted average
   * @public
   */
  public calculateAreaWeightedAverage(items: { Area: number | undefined, Average: number | undefined }[]): number {
    const filteredItems = items.filter(x => !!x.Average);
    const totalArea = filteredItems.reduce((areaSum, field) => areaSum + Create(field?.Area, 0), 0);
    const averageWithFieldsArea = filteredItems.reduce((sensorAvgSum, item) => {
      const sensorAvg = item.Average > -1 ? item.Average * Create(item?.Area, 0) : 0;
      return sensorAvgSum + sensorAvg;
    }, 0);
    if (!averageWithFieldsArea || !totalArea) {
      return 0;
    }
    return averageWithFieldsArea / totalArea;
  }

  /**
   * Generates a human-readable description of the control factor based on operation mode
   * Handles different scenarios:
   * - For map-based modes (Target Map, Application Map, Application Map Absolute), uses application settings
   * - For constant mode, returns a constant rate description
   * - For other modes, uses predefined control factor constants
   * @param operationMode The current operation mode (e.g., Constant, Target Map)
   * @param slope Numeric slope value used for determining the description
   * @param applicationSettings Optional settings that may influence the description
   * @param isPlantProtection Flag to differentiate between plant protection and other applications
   * @returns Translated string describing the control factor, or '-' if no description available
   * @public
   */
  public getControlFactorDescription(operationMode: OperationModesEnum, slope: number,
                                     applicationSettings: IApplicationSettings | undefined,
                                     isPlantProtection: boolean): string {
    if ((operationMode === OperationModesEnum.PpTargetMapPerField
      || operationMode === OperationModesEnum.PPTargetMapPerPlan
      || operationMode === OperationModesEnum.ApplicationMap
      || operationMode === OperationModesEnum.ApplicationMapAbsolute) && applicationSettings) {
      const parameters = applicationSettings.Parameter;
      if (!parameters || !parameters.slope?.Any()) {
        return '-';
      }
      const slopeKey = parameters.slope.FirstOrDefault(s => s.Slope === slope)?.Key;
      if (parameters.slopeType === 'dropDown' && !!slopeKey) {
        return this.translationService.translate(slopeKey);
      }
      return '-';
    } else if (operationMode === OperationModesEnum.Constant) {
      return this.translationService.translate('Global__RateConst');
    } else {
      const defaultSlopeKey = this.getDefaultSlopeKey(slope, isPlantProtection);
      if (!!defaultSlopeKey) {
        return this.translationService.translate(defaultSlopeKey);
      }
      return '-';
    }
  }

  /**
   * Retrieves the default slope key based on type of application
   * @param slope Slope value
   * @param isPlantProtection Flag indicating if it's plant protection
   * @returns Translated slope key
   * @private
   */
  private getDefaultSlopeKey(slope: number, isPlantProtection: boolean): string {
    if (isPlantProtection) {
      return PpConstants.PpControlFactors.FirstOrDefault(c => c.Id === slope)?.Key;
    } else {
      return slope === -1 ? 'Global__Quality_Oriented' : 'Global__Yield_Oriented';
    }
  }
}
